import { normalizePhoneE164 } from './signatures.js';
export const PROFILE_ONBOARDING_STEPS = ['address', 'birthDate', 'email', 'phone', 'children', 'emergency', 'bank', 'signature'];
const has = (v) => typeof v === 'string' ? v.trim().length > 0 : Array.isArray(v) ? v.length > 0 : !!v;
/** A real calendar date as YYYY-MM-DD. */
export function isIsoDate(v) {
    if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v))
        return false;
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}
/** One emergency contact counts once name, relationship and phone are all there. */
export function emergencyContactComplete(c) {
    const o = (c ?? {});
    return has(o.name) && has(o.relationship) && has(o.phone);
}
/** Emergency contacts complete: at least one, and every listed contact complete. */
export function emergencyContactsComplete(list) {
    return Array.isArray(list) && list.length > 0 && list.every(emergencyContactComplete);
}
/** Bank details complete: account owner, IBAN, BIC/SWIFT and bank name. */
export function bankDetailsComplete(user) {
    return has(user.accountOwnerName) && has(user.iban) && has(user.bicSwift) && has(user.bankName);
}
/** Swiss employee → children are mandatory (AHV / family allowances). */
export function isSwissEmployee(user) {
    return (user.country ?? '').toUpperCase() === 'CH' || /\bAG$/.test((user.employmentEntity ?? '').trim());
}
export function profileOnboardingStatus(user) {
    const st = user.profileOnboarding ?? null;
    const skipped = (k) => st?.skipped?.[k] ?? 0;
    const swiss = isSwissEmployee(user);
    const steps = [
        { key: 'address', label: 'Address', mandatory: true, done: has(user.address) && has(user.zipCode) && has(user.city) && has(user.country), muted: false },
        { key: 'birthDate', label: 'Date of birth', mandatory: true, done: isIsoDate(user.dateOfBirth), muted: false },
        { key: 'email', label: 'Private email', mandatory: true, done: has(user.privateEmail), muted: false },
        // A mobile number WITH country code (Oct 2026): codes for signing contracts go there by text message.
        { key: 'phone', label: 'Mobile number', mandatory: true, done: !!normalizePhoneE164(user.privatePhone), muted: false },
        { key: 'children', label: 'Children', mandatory: swiss, done: has(user.children) || st?.noChildren === true, muted: !swiss && skipped('children') >= 2 },
        // Mandatory and complete since 26 Oct 2026 (name, relationship and phone each); the old "no contact" opt-out no longer counts.
        { key: 'emergency', label: 'Emergency contacts', mandatory: true, done: emergencyContactsComplete(user.emergencyContacts), muted: false },
        { key: 'bank', label: 'Bank details', mandatory: true, done: bankDetailsComplete(user), muted: false },
        { key: 'signature', label: 'Signature', mandatory: false, done: has(user.savedSignature), muted: skipped('signature') >= 2 },
    ];
    const missingMandatory = steps.filter((s) => s.mandatory && !s.done);
    const openOptional = steps.filter((s) => !s.mandatory && !s.done && !s.muted);
    return { steps, missingMandatory, openOptional, complete: missingMandatory.length === 0 && openOptional.length === 0 };
}
/** Employees only (Michael, 9 Oct 2026: externals and agents don't need bank details; 26 Oct 2026: Employed,
 *  Oyster and Remote, so External Employed is out). A missing employment type counts as employee. */
const EMPLOYEE_TYPES = new Set(['Employed', 'Oyster', 'Remote']);
export function isProfileOnboardingAudience(user) {
    // A team / shared account (accounting@, hr@ …) has no personal data to complete.
    if (user.profileKind === 'shared')
        return false;
    return user.active !== false && (!user.employmentType || EMPLOYEE_TYPES.has(user.employmentType));
}
/** Is the walk-through switched on for this person? Employees only, then the rollout switch. */
export function profileOnboardingEnabledFor(config, isAdmin, user) {
    if (!isProfileOnboardingAudience(user))
        return false;
    const r = config?.profileOnboarding ?? 'off';
    return r === 'all' || (r === 'admins' && isAdmin);
}
/** Hard gate: the walk-through is on for this person and a mandatory step is still open. */
export function profileOnboardingBlocks(enabled, status) {
    return enabled && !!status && status.missingMandatory.length > 0;
}
/** The fields HR must have on file before a new hire can be activated (26 Oct 2026). Bank details and
 *  emergency contacts are not in here: the person fills them through the walk-through on first sign-in.
 *  Returns the labels of what is missing (empty = ready). Only applies to the walk-through audience. */
export function newHireActivationMissing(user) {
    if (!isProfileOnboardingAudience({ employmentType: user.employmentType, active: true, profileKind: user.profileKind }))
        return [];
    const missing = [];
    if (!has(user.displayName))
        missing.push('Display name');
    if (!has(user.payoutCurrency))
        missing.push('Payout currency');
    if (!has(user.employmentType))
        missing.push('Employment type');
    if (!isIsoDate(user.dateOfBirth))
        missing.push('Date of birth');
    if (!has(user.privateEmail))
        missing.push('Private email');
    if (!normalizePhoneE164(user.privatePhone))
        missing.push('Mobile number (with country code)');
    return missing;
}
/** Should the pop-up open by itself now? Once a day at most; while something is missing (fill mode) or, for a
 *  complete profile, until the person has confirmed their details once (review mode, "Check your details"). */
export function profileOnboardingShouldAutoOpen(status, state, nowMs) {
    if (status.complete && state?.reviewedAt)
        return false;
    const DAY = 20 * 60 * 60 * 1000;
    const last = Math.max(state?.dismissedAt?.toMillis?.() ?? 0, state?.shownAt?.toMillis?.() ?? 0);
    return nowMs - last > DAY;
}
/** Review still owed: everything is on file but the person has not confirmed it yet. */
export function profileOnboardingNeedsReview(status, state) {
    return status.complete && !state?.reviewedAt;
}
//# sourceMappingURL=profileOnboarding.js.map